// useAudioWave.ts
import { useEffect, useMemo, useRef, useState } from "react";

type Options = {
  active: boolean;   // Only true when you WANT animation (user or AI speaking)
  bars?: number;
  gain?: number;      // Amplification factor for visual responsiveness
  smoothing?: number; // 0..1 analyser smoothing; lower = more reactive
  exponent?: number;  // Non-linear boost for low amplitudes (e.g., 0.5 = sqrt)
};

export function useAudioWave({ active, bars = 24, gain = 3.5, smoothing = 0.65, exponent = 0.5 }: Options) {
  // Stable "flat" array so React doesn't jitter
  const flat = useMemo(() => Array(bars).fill(1), [bars]);
  const [waveData, setWaveData] = useState<number[]>(flat);

  const streamRef = useRef<MediaStream | null>(null);
  const rafRef = useRef<number | null>(null);
  const ctxRef = useRef<AudioContext | null>(null);

  useEffect(() => {
    // If not active: force flat, ensure EVERYTHING is closed/stopped.
    if (!active) {
      setWaveData(flat);

      if (rafRef.current) { cancelAnimationFrame(rafRef.current); rafRef.current = null; }
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(t => t.stop());
        streamRef.current = null;
      }
      if (ctxRef.current) { ctxRef.current.close(); ctxRef.current = null; }
      return;
    }

    let analyser: AnalyserNode | null = null;
    let source: MediaStreamAudioSourceNode | null = null;

    const start = async () => {
      const ACtx = (window as any).AudioContext || (window as any).webkitAudioContext;
      const ctx = new ACtx();
      ctxRef.current = ctx;

      // open mic ONLY while active
      streamRef.current = await navigator.mediaDevices.getUserMedia({
        audio: {
          noiseSuppression: true,
          echoCancellation: true,
          autoGainControl: false,
          channelCount: 1,
        } as MediaTrackConstraints,
      });

      analyser = ctx.createAnalyser();
      analyser.fftSize = 1024;
      analyser.smoothingTimeConstant = smoothing;

      source = ctx.createMediaStreamSource(streamRef.current);
      source.connect(analyser);

      const time = new Float32Array(analyser.fftSize);

      const tick = () => {
        if (!analyser) return;
        analyser.getFloatTimeDomainData(time);

        // Map → bars using MAX energy per segment for snappier response
        const step = Math.floor(time.length / bars) || 1;
        const next = Array.from({ length: bars }, (_, i) => {
          const startIndex = i * step;
          const endIndex = Math.min(startIndex + step, time.length);
          let maxAbs = 0;
          for (let j = startIndex; j < endIndex; j++) {
            const a = Math.abs(time[j]);
            if (a > maxAbs) maxAbs = a;
          }
          const boosted = Math.pow(maxAbs, exponent);
          const scaled = 1 + boosted * gain; // baseline 1, grow with signal
          // Clamp to avoid excessively tall bars
          return Math.min(scaled, 4);
        });
        setWaveData(next);
        rafRef.current = requestAnimationFrame(tick);
      };

      rafRef.current = requestAnimationFrame(tick);
    };

    start();

    return () => {
      if (rafRef.current) { cancelAnimationFrame(rafRef.current); rafRef.current = null; }
      if (source) source.disconnect();
      if (analyser) analyser.disconnect();
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(t => t.stop());
        streamRef.current = null;
      }
      if (ctxRef.current) { ctxRef.current.close(); ctxRef.current = null; }
    };
  }, [active, bars, flat, gain, smoothing, exponent]);

  return waveData;
}
//working